import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { entries } from '../../../helpers/common';
import {
  usePromiseWrapper,
  useStoreSelector,
  useCache,
  useActions,
} from '../../../helpers/hooks';
import {
  fetchPayMethods,
  fetchPayWaysForPayMethod,
} from '../../../actions/payMethods';
import { selectPayWay, fetchPayWay } from '../../../actions/payWays';

import PayMethodItem from './PayMethodItem';
import { ErrorBlock } from '../../Block';
import Loader from '../../Loader';

export default function PayMethodsList() {
  const actions = useActions(
    { fetchPayMethods, fetchPayWay, fetchPayWaysForPayMethod, selectPayWay },
    []
  );

  const payMethods = useStoreSelector((selector) => selector.getPayMethods());
  const payment = useStoreSelector((selector) => selector.getPayment());

  const validateCache = useCache();

  const [
    wrappedFetchPayMethods,
    payMethodsAreLoading,
    payMethodsFetchError,
  ] = usePromiseWrapper({
    initPromise: actions.fetchPayMethods,
    cached() {
      return validateCache('payMethods');
    },
  });

  useEffect(() => {
    wrappedFetchPayMethods();
  }, []);

  const { t } = useTranslation();

  const [animationOut, setAnimationOut] = useState(false);
  function moveOutAnimation() {
    setAnimationOut(true);
  }

  let content;

  if (payMethodsAreLoading) {
    content = <Loader />;
  } else if (payMethodsFetchError) {
    content = (
      <ErrorBlock headerText={t('Error')} text={t('PayMethods.Error')}>
        <button className="btn" onClick={wrappedFetchPayMethods}>
          {t('reload')}
        </button>
      </ErrorBlock>
    );
  } else {
    const filteredPayMethods = entries(payMethods).filter(
      ([, { show, defaultPayWay }]) =>
        show &&
        !(
          payment.initialPayWay != null &&
          (defaultPayWay == null || defaultPayWay.id !== payment.initialPayWay)
        )
    );

    if (filteredPayMethods.length === 0) {
      content = <ErrorBlock headerText={t('PayMethods.ZeroPayMethodsError')} />;
    } else {
      content = (
        <ul className="payments-methods__list">
          {filteredPayMethods
            .sort(
              ([, { order: order1 }], [, { order: order2 }]) => order1 - order2
            )
            .map(([id, pm]) => (
              <PayMethodItem
                key={id}
                id={id}
                {...pm}
                selectPayWay={actions.selectPayWay}
                fetchPayWay={actions.fetchPayWay}
                fetchPayWaysForPayMethod={actions.fetchPayWaysForPayMethod}
                validateCache={validateCache}
                onRedirect={moveOutAnimation}
              />
            ))}
        </ul>
      );
    }
  }

  return (
    <section
      className={classnames('payments-methods', {
        'block--moveout': animationOut,
      })}
    >
      <h3>{t('PayMethods.Method')}</h3>

      {content}
    </section>
  );
}
